/**
 * The add-vehicle flow on the Veivo path (DEV-1889): five steps, the first two
 * Veivo's (the documents and the walk-around, each a gate the next waits on),
 * the last three RideMinder's (details, drivers, and Review, where the vehicle
 * is created and its fee charged).
 */
export const V3_STEPS = [
  { step: 1, label: "Documents" },
  { step: 2, label: "Walk-around" },
  { step: 3, label: "Vehicle details" },
  { step: 4, label: "Drivers" },
  { step: 5, label: "Review" },
] as const;

export const V3_STEP_COUNT = V3_STEPS.length;

export const stepLabel = (step: number) => V3_STEPS.find((s) => s.step === step)?.label ?? "";

/** Every required document accepted — Veivo opened the walk-around. */
const DOCUMENTS_DONE = ["ready_for_capture", "capture_complete", "complete"];

export const documentsDone = (status: string | null | undefined) => DOCUMENTS_DONE.includes(status ?? "");

/** The walk-around and VIN photo passed: the verification is complete. */
export const walkaroundDone = (status: string | null | undefined) => status === "complete";

/** The furthest step the verification allows: a gate is not skipped. */
export function maxReachableStep(status: string | null | undefined): number {
  if (walkaroundDone(status)) return V3_STEP_COUNT;
  return documentsDone(status) ? 2 : 1;
}

/**
 * Where the page opens: where the operator left the draft, moved on past a
 * gate passed since (the phone may have finished the walk-around), and never
 * past one still closed.
 */
export function initialStep(status: string | null | undefined, savedStep: number | null | undefined): number {
  const firstOpen = walkaroundDone(status) ? 3 : documentsDone(status) ? 2 : 1;
  return Math.min(Math.max(savedStep ?? 1, firstOpen), maxReachableStep(status));
}

/**
 * The furthest step the operator may open now: the verification's gates, and
 * then RideMinder's own — Drivers needs the details, Review needs both. The
 * stepper never jumps past what the draft holds.
 */
export function furthestStep(status: string | null | undefined, data: V3DraftData): number {
  const verified = maxReachableStep(status);
  if (verified < V3_STEP_COUNT) return verified;
  return data.details ? (data.access ? 5 : 4) : 3;
}

/** What the next step asks of the operator, said where the draft waits. */
export const NEXT_HINTS: Record<number, string> = {
  1: "upload the documents",
  2: "you'll need your phone at the car",
  3: "the vehicle's details",
  4: "who can drive it",
  5: "review and add it",
};

/** Where a draft stands, by its next step: one line in its My Vehicles row. */
const DRAFT_SUMMARIES: Record<number, string> = {
  1: "Next: upload the vehicle's documents",
  2: "Documents done — walk-around next (you'll need your phone at the car)",
  3: "Checks done ✓ — just the vehicle details left to fill",
  4: "Details saved — choose who can drive it next",
  5: "Details and drivers saved — review and add it",
};

export interface DraftChecklistItem {
  step: number;
  label: string;
  state: "done" | "next" | "todo";
}

/**
 * A draft's five steps as done, next or to do (DEV-1904) — for "Save and
 * finish later" and the draft rows in My Vehicles. Done is what the server
 * holds: a passed gate, saved details, saved drivers.
 */
export function draftChecklist(status: string | null | undefined, data: V3DraftData): {
  items: DraftChecklistItem[];
  next: number;
  /** "Documents done — walk-around next (you'll need your phone at the car)" */
  summary: string;
} {
  const done = [documentsDone(status), walkaroundDone(status), !!data.details, !!data.access, false];
  const next = done.findIndex((d) => !d) + 1;
  const items = V3_STEPS.map(({ step, label }) => ({
    step,
    label,
    state: (done[step - 1] ? "done" : step === next ? "next" : "todo") as DraftChecklistItem["state"],
  }));
  return { items, next, summary: DRAFT_SUMMARIES[next] };
}

/** What a draft holds, as the steps hold it. Kept on the server (DEV-1899), so
 *  it resumes on any device. */
export interface V3DetailsDraft {
  vehicleTypeUuids: string[];
  nickname: string;
  hasMarkings: boolean;
  capacity: number;
  bags: number;
  maintenanceFee: number;
}

export interface V3Driver {
  id: string;
  name: string;
  email: string;
  avatar: string;
}

export interface V3AccessDraft {
  level: "ALL" | "RESTRICTED";
  drivers: V3Driver[];
}

export interface V3DraftData {
  details?: V3DetailsDraft;
  access?: V3AccessDraft;
}

/** A draft's data as saved, read defensively: it is the browser's own shape,
 *  stored as JSON, and may come from an older build. */
export function readDraftData(data: Record<string, unknown> | null | undefined): V3DraftData {
  const details = data?.details as Partial<V3DetailsDraft> | undefined;
  const access = data?.access as Partial<V3AccessDraft> | undefined;
  return {
    ...(details && Array.isArray(details.vehicleTypeUuids)
      ? {
          details: {
            vehicleTypeUuids: details.vehicleTypeUuids.filter((uuid): uuid is string => typeof uuid === "string"),
            nickname: typeof details.nickname === "string" ? details.nickname : "",
            hasMarkings: details.hasMarkings === true,
            capacity: typeof details.capacity === "number" ? details.capacity : 0,
            bags: typeof details.bags === "number" ? details.bags : 0,
            maintenanceFee: typeof details.maintenanceFee === "number" ? details.maintenanceFee : 0,
          },
        }
      : {}),
    ...(access && (access.level === "ALL" || access.level === "RESTRICTED")
      ? {
          access: {
            level: access.level,
            drivers: Array.isArray(access.drivers) ? access.drivers.filter((d): d is V3Driver => typeof d?.id === "string") : [],
          },
        }
      : {}),
  };
}
